import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  Typography,
  FormControl,
  Button,
  MenuItem,
  Select,
  InputLabel,
  Snackbar,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  TextField,
  FormControlLabel,
  Checkbox,
  Chip,
  Menu,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  PRIMARY_BUTTON_SX,
  SECONDARY_BUTTON_SX,
  ACTIONS_HEADER_BTN_SX,
  ACTION_MENU_PAPER_SX,
} from "../../../theme/buttonstyles";
import { useNavigate, useParams } from "react-router-dom";

import BarChartIcon from "@mui/icons-material/BarChart";
import DescriptionIcon from "@mui/icons-material/Description";
import ListAltIcon from "@mui/icons-material/ListAlt";
import LinkIcon from "@mui/icons-material/Link";
import AddIcon from "@mui/icons-material/Add";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

import api from "../../../services/api/axios";
import { BRE_SURVEY_TEMPLATES } from "./BRESurveyTemplatesRegistry";
import { useQueryClient } from "@tanstack/react-query";
import { normalizeQuestions } from "./BRESurveyHelpers";
import BRESurveyForm from "./BRESurveyForm";
import {
  fetchSurveyTemplates,
  normalizeSurveyTemplate,
  serializeSurveyQuestion,
} from "./surveyService";

const EMPTY_SURVEY = {
  title: "",
  questions: [],
};

const BRAND_BLUE = "#00336C";
const BRAND_GREEN = "#648315";
const BRAND_GREEN_HOVER = "#557012";
const PAGE_BG = "#f7f9fc";
const OPTION_TYPES = new Set(["select", "radio", "checkbox"]);

const CARD_SX = {
  border: `1.5px solid ${BRAND_BLUE}`,
  borderRadius: 2,
  boxShadow: "0 8px 20px rgba(15, 23, 42, 0.08)",
  background: "#ffffff",
  overflow: "hidden",
};


const BUILDER_INPUT_SX = {
  mb: 2,
  "& .MuiOutlinedInput-root": {
    borderRadius: 2,
    "&.Mui-focused fieldset": {
      borderColor: BRAND_BLUE,
    },
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: BRAND_BLUE,
  },
};

const BUILDER_SELECT_SX = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 2,
    "&.Mui-focused fieldset": {
      borderColor: BRAND_BLUE,
    },
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: BRAND_BLUE,
  },
};

const BUILDER_SECTION_LABEL_SX = {
  color: BRAND_BLUE,
  fontWeight: 700,
  mb: 1,
};

const BUILDER_PRIMARY_BUTTON_SX = {
  ...PRIMARY_BUTTON_SX,
  backgroundColor: BRAND_GREEN,
  "&:hover": {
    backgroundColor: BRAND_GREEN_HOVER,
  },
};

const BUILDER_OUTLINED_BUTTON_SX = {
  borderColor: BRAND_BLUE,
  color: BRAND_BLUE,
  textTransform: "none",
  fontWeight: 600,
  mt: 1,
  "&:hover": {
    borderColor: BRAND_BLUE,
    backgroundColor: "rgba(0, 51, 108, 0.05)",
  },
};

function slugify(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)+/g, "");
}

export default function BRESurveyTemplates() {
  const queryClient = useQueryClient();

  const [templates, setTemplates] = useState(() =>
    (BRE_SURVEY_TEMPLATES || []).map(normalizeSurveyTemplate),
  );
  const [selectedTemplateSlug, setSelectedTemplateSlug] = useState("");
  const [responses, setResponses] = useState({});
  const [companyName, setCompanyName] = useState("");
  const [naicsCode, setNaicsCode] = useState("");
  const [sector, setSector] = useState("");
  const [companyPhone, setCompanyPhone] = useState("");
  const [companyWebsite, setCompanyWebsite] = useState("");
  const [companyCity, setCompanyCity] = useState("");
  const [companyState, setCompanyState] = useState("");
  const [linkCopied, setLinkCopied] = useState(false);
  const [showBuilder, setShowBuilder] = useState(false);
  const [newSurvey, setNewSurvey] = useState(EMPTY_SURVEY);
  const [savingSurvey, setSavingSurvey] = useState(false);
  const [actionsAnchorEl, setActionsAnchorEl] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const { templateId: surveySlugFromUrl } = useParams();
  const navigate = useNavigate();
  const actionsMenuOpen = Boolean(actionsAnchorEl);

  const isPublic = !!surveySlugFromUrl;

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const fetchSurveys = useCallback(async () => {
    if (isPublic) return;

    try {
      const loadedTemplates = await fetchSurveyTemplates();
      if (loadedTemplates.length) {
        setTemplates(loadedTemplates);
      }
    } catch (err) {
      console.error(err);
      showSnackbar("Failed to load survey templates.", "error");
    }
  }, [isPublic]);

  useEffect(() => {
    fetchSurveys();
  }, [fetchSurveys]);

  const selectedTemplate = useMemo(() => {
    const slug = surveySlugFromUrl || selectedTemplateSlug;
    return templates.find((template) => String(template.slug) === String(slug));
  }, [templates, surveySlugFromUrl, selectedTemplateSlug]);

  useEffect(() => {
    if (surveySlugFromUrl) setSelectedTemplateSlug(surveySlugFromUrl);
  }, [surveySlugFromUrl]);

  const normQuestions = useMemo(
    () =>
      selectedTemplate ? normalizeQuestions(selectedTemplate.questions) : [],
    [selectedTemplate],
  );

  const setAnswer = (questionId, value) => {
    setResponses((prev) => ({ ...prev, [questionId]: value }));
  };

  const resetForm = (clearTemplateSelection = !isPublic) => {
    setResponses({});
    if (clearTemplateSelection) setSelectedTemplateSlug("");
    setCompanyName("");
    setSector("");
    setNaicsCode("");
    setCompanyPhone("");
    setCompanyWebsite("");
    setCompanyCity("");
    setCompanyState("");
  };

  const handleTemplateChange = (e) => {
    setSelectedTemplateSlug(e.target.value);
    resetForm(false);
  };

  const openBuilder = () => {
    setNewSurvey(EMPTY_SURVEY);
    setShowBuilder(true);
  };

  const closeBuilder = () => {
    if (savingSurvey) return;
    setShowBuilder(false);
    setNewSurvey(EMPTY_SURVEY);
  };

  const updateQuestion = (index, patch) => {
    setNewSurvey((prev) => {
      const questions = [...prev.questions];
      questions[index] = { ...questions[index], ...patch };
      return { ...prev, questions };
    });
  };

  const addQuestion = () => {
    setNewSurvey((prev) => ({
      ...prev,
      questions: [
        ...prev.questions,
        {
          id: `q${Date.now()}`,
          type: "text",
          label: "",
          required: false,
          options: [],
          optionsText: "",
        },
      ],
    }));
  };

  const removeQuestion = (index) => {
    setNewSurvey((prev) => ({
      ...prev,
      questions: prev.questions.filter((_question, questionIndex) => questionIndex !== index),
    }));
  };

  const saveDisabled = useMemo(() => {
    if (!newSurvey.title.trim() || newSurvey.questions.length === 0) return true;

    return newSurvey.questions.some((question) => {
      if (!question.label.trim()) return true;
      if (!OPTION_TYPES.has(question.type)) return false;
      return (question.options || []).filter((option) => String(option).trim()).length === 0;
    });
  }, [newSurvey]);

  const handleSaveSurvey = async () => {
    if (saveDisabled) return;

    const name = newSurvey.title.trim();
    const slug = slugify(name);
    if (!slug) {
      showSnackbar("Survey title must include at least one letter or number.", "error");
      return;
    }

    const schema = {
      title: name,
      questions: newSurvey.questions.map(serializeSurveyQuestion),
    };

    try {
      setSavingSurvey(true);
      await api.post("/survey-templates/custom", {
        name,
        slug,
        schema,
      });

      setShowBuilder(false);
      setNewSurvey(EMPTY_SURVEY);
      setSelectedTemplateSlug(slug);
      await fetchSurveys();
      showSnackbar("Survey created.");
    } catch (err) {
      console.error(err);
      const message =
        err?.response?.data?.detail ||
        "Failed to create survey. Please check the survey and try again.";
      showSnackbar(message, "error");
    } finally {
      setSavingSurvey(false);
    }
  };

  const generateSurveyLink = () => {
    if (!selectedTemplateSlug) return;
    const link = `${window.location.origin}/survey/${selectedTemplateSlug}`;
    navigator.clipboard.writeText(link).then(() => setLinkCopied(true));
  };

  const handleOpenActionsMenu = (event) => {
    setActionsAnchorEl(event.currentTarget);
  };

  const handleCloseActionsMenu = () => {
    setActionsAnchorEl(null);
  };

  const requiredMissing = useMemo(() => {
    if (!selectedTemplate) return [];

    const missing = [];
    for (const question of normQuestions) {
      if (!question.required) continue;
      const value = responses[question.id];

      if (question.type === "text" || question.type === "single") {
        if (!value || String(value).trim() === "") {
          missing.push(question.id);
        }
      } else if (question.type === "multi") {
        if (!Array.isArray(value) || value.length === 0) {
          missing.push(question.id);
        }
      }
    }

    return missing;
  }, [normQuestions, responses, selectedTemplate]);

  const handleSubmit = async () => {
    if (!selectedTemplate || !companyName.trim() || !sector.trim()) {
      alert(
        "Please select a template, enter your company name, and select a sector.",
      );
      return;
    }

    if (requiredMissing.length > 0) {
      alert("Please answer all required questions.");
      return;
    }

    try {
      const payload = {
        template_slug: selectedTemplate.slug,
        template_version: selectedTemplate.version ?? 1,
        company_name: companyName,
        sector,
        naics_code: naicsCode.trim() || null,
        company_phone_number: companyPhone,
        company_website: companyWebsite,
        company_city: companyCity,
        company_state: companyState,
        answers: responses,
      };

      await api.post("/survey-responses", payload);

      queryClient.invalidateQueries({
        queryKey: ["survey-responses-expanded"],
      });

      alert("Thank you for your feedback!");
      resetForm();
    } catch {
      alert("Error submitting. Please try again.");
    }
  };

  const statsPath = selectedTemplateSlug
    ? `/survey-stats?templateId=${encodeURIComponent(selectedTemplateSlug)}`
    : "/survey-stats";

  const surveyForm = selectedTemplate ? (
    <BRESurveyForm
      normQuestions={normQuestions}
      responses={responses}
      setAnswer={setAnswer}
      companyName={companyName}
      setCompanyName={setCompanyName}
      sector={sector}
      setSector={setSector}
      naicsCode={naicsCode}
      setNaicsCode={setNaicsCode}
      companyPhone={companyPhone}
      setCompanyPhone={setCompanyPhone}
      companyWebsite={companyWebsite}
      setCompanyWebsite={setCompanyWebsite}
      companyCity={companyCity}
      setCompanyCity={setCompanyCity}
      companyState={companyState}
      setCompanyState={setCompanyState}
      handleSubmit={handleSubmit}
      submitDisabled={!selectedTemplate || !companyName.trim() || !sector.trim()}
      submitButtonSx={SECONDARY_BUTTON_SX}
    />
  ) : null;

  if (isPublic) {
    return (
      <Box sx={{ p: 2, width: "100%", maxWidth: 900, mx: "auto" }}>
        {selectedTemplate ? (
          <Box sx={{ p: 3, borderRadius: 2 }}>
            <Typography
              variant="h4"
              fontWeight="bold"
              gutterBottom
              sx={{ textAlign: "center" }}
            >
              {selectedTemplate.name || "Survey"}
            </Typography>

            {surveyForm}
          </Box>
        ) : (
          <Typography align="center" sx={{ mt: 6 }}>
            This survey link is invalid or missing a template.
          </Typography>
        )}

        <Snackbar
          open={linkCopied}
          autoHideDuration={3000}
          onClose={() => setLinkCopied(false)}
        >
          <Alert
            onClose={() => setLinkCopied(false)}
            severity="success"
            sx={{ width: "100%" }}
          >
            Survey link copied to clipboard!
          </Alert>
        </Snackbar>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", minHeight: "100vh", backgroundColor: PAGE_BG, pb: 4 }}>
      {/* ===== Sticky Header ===== */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 5,
          borderBottom: "1px solid #dce4ef",
          background: "#ffffff",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            px: { xs: 2, md: 3 },
            py: 1.75,
            flexWrap: "wrap",
          }}
        >
          {/* Left: icon + title */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexShrink: 0 }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: 2,
                background: BRAND_GREEN,
                color: "#ffffff",
                display: "grid",
                placeItems: "center",
                flex: "0 0 auto",
              }}
            >
              <DescriptionIcon sx={{ fontSize: 19 }} />
            </Box>
            <Typography
              sx={{
                fontSize: "1.05rem",
                fontWeight: 800,
                color: BRAND_BLUE,
                letterSpacing: "-0.01em",
                whiteSpace: "nowrap",
              }}
            >
              Survey Templates
            </Typography>
          </Box>

          <Box sx={{ flex: 1 }} />

          {/* Right: actions */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
            <Button
              endIcon={<KeyboardArrowDownIcon />}
              onClick={handleOpenActionsMenu}
              sx={ACTIONS_HEADER_BTN_SX}
            >
              Actions
            </Button>
            <Menu
              anchorEl={actionsAnchorEl}
              open={actionsMenuOpen}
              onClose={handleCloseActionsMenu}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              PaperProps={{ sx: ACTION_MENU_PAPER_SX }}
            >
              <MenuItem onClick={() => { handleCloseActionsMenu(); openBuilder(); }}>
                <ListItemIcon sx={{ minWidth: 32, color: BRAND_GREEN }}>
                  <AddIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Create Survey</ListItemText>
              </MenuItem>
              <MenuItem onClick={() => { handleCloseActionsMenu(); navigate("/survey-responses"); }}>
                <ListItemIcon sx={{ minWidth: 32, color: "#0F766E" }}>
                  <ListAltIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Responses</ListItemText>
              </MenuItem>
              <MenuItem onClick={() => { handleCloseActionsMenu(); navigate(statsPath); }}>
                <ListItemIcon sx={{ minWidth: 32, color: "#0F766E" }}>
                  <BarChartIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Stats</ListItemText>
              </MenuItem>
              <MenuItem
                onClick={() => { handleCloseActionsMenu(); generateSurveyLink(); }}
                disabled={!selectedTemplateSlug}
              >
                <ListItemIcon sx={{ minWidth: 32, color: "#0E7490" }}>
                  <LinkIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Share Link</ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        </Box>
      </Box>

      {/* ===== Content Area ===== */}
      <Box sx={{ px: { xs: 2, md: 3 }, mt: 3 }}>
        <Box sx={CARD_SX}>
          {/* Template selector section */}
          <Box
            sx={{
              px: { xs: 2, sm: 3 },
              pt: { xs: 2, sm: 2.5 },
              pb: { xs: 2, sm: 2.5 },
              borderBottom: "1px solid #dce4ef",
              background: "linear-gradient(135deg, rgba(0,51,108,0.03) 0%, rgba(100,131,21,0.03) 100%)",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
              <DescriptionIcon sx={{ color: BRAND_BLUE, fontSize: 18 }} />
              <Typography
                sx={{ fontWeight: 700, color: BRAND_BLUE, fontSize: "0.875rem", letterSpacing: "0.02em", textTransform: "uppercase" }}
              >
                Survey Template
              </Typography>
            </Box>

            <FormControl fullWidth>
              <Select
                displayEmpty
                value={selectedTemplateSlug}
                onChange={handleTemplateChange}
                renderValue={(value) => {
                  if (!value) {
                    return (
                      <Typography sx={{ color: "rgba(0,0,0,0.38)", fontSize: "0.95rem" }}>
                        Choose a template to begin…
                      </Typography>
                    );
                  }
                  const t = templates.find((tmpl) => tmpl.slug === value);
                  return (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <DescriptionIcon sx={{ fontSize: 17, color: BRAND_BLUE, flexShrink: 0 }} />
                      <Typography sx={{ fontWeight: 600, color: BRAND_BLUE, fontSize: "0.95rem" }}>
                        {t ? t.name : value}
                      </Typography>
                      {t?.is_custom && (
                        <Chip label="Custom" size="small" color="primary" variant="outlined" sx={{ height: 20, fontSize: 11 }} />
                      )}
                    </Box>
                  );
                }}
                sx={{
                  borderRadius: 2,
                  backgroundColor: "#ffffff",
                  fontSize: "0.95rem",
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#dce4ef", borderWidth: "1.5px" },
                  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BRAND_BLUE },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: BRAND_BLUE, borderWidth: 2 },
                  "& .MuiSelect-select": { py: 1.5, px: 2 },
                }}
              >
                {templates.map((template) => (
                  <MenuItem
                    key={template.slug}
                    value={template.slug}
                    sx={{
                      py: 1.25,
                      px: 2,
                      "&.Mui-selected": { backgroundColor: "rgba(0,51,108,0.06)" },
                      "&.Mui-selected:hover": { backgroundColor: "rgba(0,51,108,0.1)" },
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, width: "100%" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                        <DescriptionIcon sx={{ fontSize: 16, color: BRAND_BLUE, opacity: 0.6 }} />
                        <Typography sx={{ fontSize: "0.9rem", fontWeight: 500 }}>{template.name}</Typography>
                      </Box>
                      {template.is_custom && (
                        <Chip label="Custom" size="small" color="primary" variant="outlined" sx={{ height: 20, fontSize: 11 }} />
                      )}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Form section */}
          {selectedTemplate ? (
            <Box sx={{ px: { xs: 2, sm: 3 }, pt: 3, pb: { xs: 2, sm: 3 } }}>
              {surveyForm}
            </Box>
          ) : (
            <Box sx={{ py: 8, textAlign: "center" }}>
              <DescriptionIcon sx={{ fontSize: 52, color: "rgba(0,51,108,0.12)", mb: 1.5, display: "block", mx: "auto" }} />
              <Typography sx={{ color: "rgba(0,0,0,0.35)", fontSize: "0.95rem", fontWeight: 500 }}>
                Select a template above to get started
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      <Dialog
        open={showBuilder}
        onClose={closeBuilder}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            border: `2px solid ${BRAND_BLUE}`,
            borderRadius: 3,
            boxShadow: 4,
            overflow: "hidden",
            backgroundColor: BRAND_BLUE,
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: BRAND_BLUE,
            color: "#fff",
            fontWeight: 700,
            px: 3,
            py: 2,
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
          }}
        >
          Create Survey
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            p: 3,
            backgroundColor: "#fafafa",
            borderTop: 0,
          }}
        >
          <Typography sx={BUILDER_SECTION_LABEL_SX}>Survey Details</Typography>
          <TextField
            label="Survey Title"
            fullWidth
            value={newSurvey.title}
            onChange={(e) =>
              setNewSurvey((prev) => ({ ...prev, title: e.target.value }))
            }
            sx={BUILDER_INPUT_SX}
          />

          <Typography sx={{ ...BUILDER_SECTION_LABEL_SX, mt: 1 }}>
            Questions
          </Typography>

          {newSurvey.questions.map((question, index) => (
            <Paper
              key={question.id}
              sx={{
                p: 2,
                mb: 2,
                borderRadius: 2,
                border: "1px solid #e0e0e0",
                backgroundColor: "#fff",
                transition: "box-shadow 0.2s ease, border-color 0.2s ease",
                "&:hover": {
                  boxShadow: 3,
                  borderColor: "rgba(0, 51, 108, 0.35)",
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                  mb: 1.5,
                }}
              >
                <Typography sx={{ color: BRAND_BLUE, fontWeight: 700 }}>
                  Question {index + 1}
                </Typography>
                <Button
                  color="error"
                  onClick={() => removeQuestion(index)}
                  sx={{ textTransform: "none", fontWeight: 600 }}
                >
                  Remove
                </Button>
              </Box>

              <TextField
                label="Question Label"
                fullWidth
                value={question.label}
                onChange={(e) => updateQuestion(index, { label: e.target.value })}
                sx={BUILDER_INPUT_SX}
              />

              <FormControl fullWidth sx={{ ...BUILDER_SELECT_SX, mb: 1 }}>
                <InputLabel id={`question-type-${question.id}`}>Question Type</InputLabel>
                <Select
                  labelId={`question-type-${question.id}`}
                  label="Question Type"
                  value={question.type}
                  onChange={(e) =>
                    updateQuestion(index, {
                      type: e.target.value,
                      options: OPTION_TYPES.has(e.target.value)
                        ? question.options || []
                        : [],
                      optionsText: OPTION_TYPES.has(e.target.value)
                        ? question.optionsText || ""
                        : "",
                    })
                  }
                >
                  <MenuItem value="text">Text</MenuItem>
                  <MenuItem value="textarea">Text Area</MenuItem>
                  <MenuItem value="select">Select</MenuItem>
                  <MenuItem value="radio">Radio</MenuItem>
                  <MenuItem value="checkbox">Checkbox</MenuItem>
                </Select>
              </FormControl>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={question.required}
                    onChange={(e) =>
                      updateQuestion(index, { required: e.target.checked })
                    }
                    sx={{
                      color: BRAND_BLUE,
                      "&.Mui-checked": {
                        color: BRAND_GREEN,
                      },
                    }}
                  />
                }
                label="Required"
              />

              {OPTION_TYPES.has(question.type) && (
                <TextField
                  label="Options (comma separated)"
                  fullWidth
                  value={question.optionsText ?? question.options?.join(", ") ?? ""}
                  onChange={(e) => {
                    const optionsText = e.target.value;
                    updateQuestion(index, {
                      optionsText,
                      options: optionsText
                        .split(",")
                        .map((option) => option.trim())
                        .filter(Boolean),
                    });
                  }}
                  sx={{ ...BUILDER_INPUT_SX, mt: 1, mb: 0 }}
                />
              )}
            </Paper>
          ))}

          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={addQuestion}
            sx={BUILDER_OUTLINED_BUTTON_SX}
          >
            Add Question
          </Button>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, gap: 1, backgroundColor: "#fff" }}>
          <Button onClick={closeBuilder} disabled={savingSurvey}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveSurvey}
            disabled={saveDisabled || savingSurvey}
            sx={BUILDER_PRIMARY_BUTTON_SX}
          >
            {savingSurvey ? "Saving..." : "Save Survey"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={linkCopied}
        autoHideDuration={3000}
        onClose={() => setLinkCopied(false)}
      >
        <Alert
          onClose={() => setLinkCopied(false)}
          severity="success"
          sx={{ width: "100%" }}
        >
          Survey link copied to clipboard!
        </Alert>
      </Snackbar>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
